/**
 * Two calm groups beside the secrets list: the nodes a person lets run their work (which a
 * personal secret that follows them needs; an administrator also sees and assigns everyone, from
 * the people picker), and, for administrators, the organisation's own rules for secrets, served to
 * every workbench whether or not a policy is published.
 *
 * List groups (Layout.tsx): a node is a row with its state and a switch for "runs my work"; an
 * administrator's "who it runs work for" opens in a sheet from the row's menu. A rule is a row with
 * its choice at the end, saved as soon as it is chosen.
 */

import type { NodeDelegate, NodeHeld, OrgSecretsRules } from '@aioe/shared';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark, Toggle } from './ui.tsx';
import { FormSheet } from './ui/FormSheet.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

/** "GITHUB_TOKEN, NPM_TOKEN and SENTRY_DSN": a short list in words, the rest counted. */
function namesInWords(names: string[], most = 4): string {
  const shown = names.slice(0, most);
  const rest = names.length - shown.length;
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`;
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : (shown[0] ?? '');
}

/** "Holds 3 secrets for you now (GITHUB_TOKEN, NPM_TOKEN and SENTRY_DSN)", as the node last reported it. */
function heldWords(held: Pick<NodeHeld, 'names'> | undefined, forWhom: string): string | undefined {
  if (!held || held.names.length === 0) return undefined;
  const n = held.names.length;
  return `Holds ${n === 1 ? 'one secret' : `${n} secrets`} for ${forWhom} now (${namesInWords(held.names)})`;
}

export function MyNodesPanel() {
  const data = useLoad(() => routes.secretNodes(), []);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [open, setOpen] = useState<string>();
  const me = data.data?.me;
  const admin = data.data?.admin === true;
  const nodes = data.data?.nodes ?? [];
  if (!data.data || nodes.length === 0) return null;
  const rows = (nodeId: string) => data.data!.delegates.filter((d) => d.nodeId === nodeId);
  const heldBy = (nodeId: string, principal: string | undefined) => data.data!.held?.find((h) => h.nodeId === nodeId && h.principal === principal);
  const change = async (nodeId: string, principal: string, on: boolean) => {
    setBusy(`${nodeId}/${principal}`);
    setError(undefined);
    try {
      await routes.letNodeRunMyWork(nodeId, principal, on);
      data.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const opened = nodes.find((n) => n.deviceId === open);
  return (
    <>
      <ListGroup
        title="Nodes that run your work"
        count={nodes.length}
        description={admin ? 'Who each node may run work for. A secret of someone’s that says “Follow me” is given to the nodes they let run their work; you may assign anyone. A node’s operator can read what a node is given.' : 'A secret of yours that says “Follow me” is given to these nodes while they run work for you. A node’s operator can read what a node is given.'}
      >
        {error || data.error ? (
          <ListBlock>
            <ErrorNote error={error ?? data.error} />
          </ListBlock>
        ) : null}
        {nodes.map((n) => {
          const mine = rows(n.deviceId).find((d) => d.principal === me);
          const others = rows(n.deviceId).filter((d) => d.principal !== me);
          const name = n.name ?? n.hostname ?? n.deviceId;
          const holds = heldWords(heldBy(n.deviceId, me), 'you');
          return (
            <ListRow
              key={n.deviceId}
              title={
                <>
                  {name}
                  {n.hostname && n.hostname !== name ? <span className="mono ms-2 text-xs font-normal text-muted">{n.hostname}</span> : null}
                </>
              }
              description={
                <>
                  <span className="block">
                    {mine ? (mine.how === 'assigned' ? 'Assigned to run your work by an administrator' : 'You let it run your work') : 'Does not run your work'}
                    {admin && others.length > 0 ? ` · also ${others.map((d) => d.name ?? d.principal).join(', ')}` : ''}
                  </span>
                  {holds ? <span className="block">{holds}</span> : null}
                </>
              }
              status={<StatusMark state={n.online ? 'online' : 'offline'} label={n.online ? 'Online' : 'Offline'} />}
              compactControl
              control={
                <>
                  <Toggle label={`Let ${name} run my work`} checked={!!mine} isDisabled={!me || busy === `${n.deviceId}/${me}`} onChange={(on) => me && void change(n.deviceId, me, on)} />
                  {admin ? <RowMenu label={`More for ${name}`} items={[{ id: 'people', label: 'Who it runs work for' }]} onAction={() => setOpen(n.deviceId)} /> : null}
                </>
              }
            />
          );
        })}
      </ListGroup>
      {admin && opened ? (
        <FormSheet
          isOpen
          width="md"
          title={`Who ${opened.name ?? opened.hostname ?? 'this node'} runs work for`}
          description="Each person here has their secrets that follow them given to this node. Changes save at once."
          onClose={() => setOpen(undefined)}
          footer={
            <Button size="sm" variant="tertiary" onPress={() => setOpen(undefined)}>
              Done
            </Button>
          }
        >
          <NodePeople nodeId={opened.deviceId} name={opened.name ?? opened.hostname ?? opened.deviceId} me={me} delegates={rows(opened.deviceId).filter((d) => d.principal !== me)} held={data.data.held ?? []} people={data.data.people ?? []} busy={busy} onChange={change} />
          <ErrorNote error={error} />
        </FormSheet>
      ) : null}
    </>
  );
}

/** An administrator's view of one node's people: who it runs work for (with how), and the organisation's people to assign. */
function NodePeople({ nodeId, name, me, delegates, held, people, busy, onChange }: { nodeId: string; name: string; me?: string; delegates: NodeDelegate[]; held: NodeHeld[]; people: Array<{ id: string; name: string }>; busy?: string; onChange: (nodeId: string, principal: string, on: boolean) => Promise<void> }) {
  const choices = people.filter((p) => p.id !== me && !delegates.some((d) => d.principal === p.id));
  const [pick, setPick] = useState<string>();
  const holdsFor = (principal: string) => heldWords(held.find((h) => h.nodeId === nodeId && h.principal === principal), 'them');
  return (
    <div className="grid gap-4">
      {choices.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <ChoiceSelect label="Assign someone" placeholder="Assign someone" className="min-w-0 flex-1" value={pick} options={Object.fromEntries(choices.map((p) => [p.id, p.name]))} onChange={(v) => setPick(v)} />
          <Button size="sm" variant="secondary" isDisabled={!pick} isPending={!!pick && busy === `${nodeId}/${pick}`} onPress={() => pick && void onChange(nodeId, pick, true).then(() => setPick(undefined))}>
            Assign
          </Button>
        </div>
      )}
      {delegates.length === 0 ? (
        <p className="text-sm text-muted">Nobody else has let {name} run their work, and you have assigned nobody.</p>
      ) : (
        <ListGroup>
          {delegates.map((d) => (
            <ListRow
              key={d.principal}
              title={d.name ?? d.principal}
              description={
                <>
                  <span className="block">{d.how === 'assigned' ? 'Assigned by an administrator' : 'Their own say'}</span>
                  {holdsFor(d.principal) ? <span className="block">{holdsFor(d.principal)}</span> : null}
                </>
              }
              compactControl
              control={
                <Button size="sm" variant="ghost" isPending={busy === `${nodeId}/${d.principal}`} onPress={() => void onChange(nodeId, d.principal, false)}>
                  Remove
                </Button>
              }
            />
          ))}
        </ListGroup>
      )}
    </div>
  );
}

const RULES: Array<{ key: keyof OrgSecretsRules; label: string; hint: string; options: Array<[string, string]> }> = [
  { key: 'personalSecrets', label: 'People’s own secrets', hint: 'Whether a person’s own value may be used in your projects, where nothing shared carries the name or the shared one allows it.', options: [['allow', 'Allowed'], ['deny', 'Not used']] },
  { key: 'personalOnNodes', label: 'Following people to nodes', hint: 'Whether a person’s secret that says “Follow me” may be given to a node they let run their work.', options: [['deny', 'Not allowed'], ['allow', 'Allowed']] },
  { key: 'nodeDelegation', label: 'Nodes act only for people who agreed', hint: 'A node is given secrets only for people who let it run their work (or you assigned). Just their own: only a person’s secrets that follow them. Every secret: shared ones too, so a node on a project cannot ask for a member who never agreed.', options: [['personal', 'Just their own'], ['all', 'Every secret']] },
  { key: 'agentEnvironment', label: 'Secrets in an agent’s environment', hint: 'The only use an agent can read. Not allowed withholds every such secret, whatever it says.', options: [['allow', 'Allowed'], ['deny', 'Not allowed']] },
  { key: 'machineEnvironment', label: 'A machine’s own environment in tool settings', hint: 'Whether a repository’s tool servers may read the machine’s environment variables, or only kept secrets.', options: [['allow', 'Allowed'], ['deny', 'Only kept secrets']] },
  { key: 'requireKeyStore', label: 'Machines without a key store', hint: 'A machine with no operating-system key store (a Linux box without a keyring) may keep shared secrets, or is refused them.', options: [['false', 'May keep them'], ['true', 'Refused them']] },
];

export function SecretsRulesPanel() {
  const data = useLoad(() => routes.secretsPolicy().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  if (!data.data) return null;
  const p = data.data.policy;
  const save = async (key: keyof OrgSecretsRules, raw: string) => {
    setError(undefined);
    try {
      await routes.setSecretsPolicy({ ...p, [key]: key === 'requireKeyStore' ? raw === 'true' : raw } as OrgSecretsRules);
      data.reload();
    } catch (e) {
      setError(problemText(e));
    }
  };
  return (
    <ListGroup title="Rules for secrets" description={data.data.set ? 'Served to every workbench and node, unless a published policy sets its own. A choice saves at once.' : 'The defaults. Change one and it is served to every workbench and node, unless a published policy sets its own.'}>
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {RULES.map((r) => (
        <ListRow key={r.key} title={r.label} description={r.hint} control={<ChoiceSelect label={r.label} className="w-48 max-w-full" value={String(p[r.key])} options={Object.fromEntries(r.options.map(([v, t]) => [v, t]))} onChange={(v) => void save(r.key, v)} />} />
      ))}
    </ListGroup>
  );
}
